import { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { logger } from '../../services/logger/logger';
import { showErrorToast } from '../../components/common/BaseToaster';
import { RootState } from '../index'; // adjust path if needed

// ---------------------
// Store Reference
// ---------------------
let store: { getState: () => RootState } | undefined;

export const setAxiosStore = (reduxStore: typeof store) => {
  store = reduxStore;
};

// ---------------------
// Session Expired Flag
// ---------------------
let sessionExpiredToastShown = false;

// ---------------------
// API Config
// ---------------------
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
/** Request timeout in ms, 30 seconds */
const REQUEST_TIMEOUT_MS = 30_000;

type AxiosBaseQueryArgs = {
  url: string;
  method: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  contentType?: 'MULTIPART' | 'JSON';
  // Optional success toaster per-call
  successToast?: { title: string; description?: string | string[] };
};

// ---------------------
// Axios Instance
// ---------------------
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------
// Request Interceptor
// ---------------------
apiClient.interceptors.request.use(
  (config: any & { contentType?: 'MULTIPART' | 'JSON' }) => {
    if (!store) return config;

    const state: RootState | any = store.getState();
    const token = state.user?.accessToken;

    // For FormData uploads, let the browser set the correct multipart boundary.
    if (config.contentType === 'MULTIPART') {
      if (config.headers) {
        delete (config.headers as any)['Content-Type'];
      }
    }

    // Attach access token
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------
// Response Interceptor
// ---------------------
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const status = error?.response?.status;
    const reqData = error?.response?.data;
    const originalRequest = error?.config;
    const isTimeout = error?.code === 'ECONNABORTED';

    if (isTimeout && !reqData?.skipToastMsg) {
      showErrorToast('Request timed out', 'The server took too long to respond. Please try again.');
      return Promise.reject({
        message: 'Request timed out',
        status: 408,
        data: reqData,
      });
    }

    switch (status) {
      case 401:
        // Prevent infinite retry loops
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          // Try to refresh the token silently
          if (store) {
            try {
              // Dynamically import MSAL to avoid circular dependencies
              const { msalInstance } = await import('../../config/msalConfig');
              const accounts = msalInstance.getAllAccounts();

              if (accounts && accounts.length > 0) {
                const account = accounts[0];
                const { azureApiScope } = await import('../../config/msalConfig');
                if (!azureApiScope) throw new Error('API scope not configured');
                const response = await msalInstance.acquireTokenSilent({
                  scopes: [azureApiScope],
                  account: account,
                });

                // Update the token in the store
                const { setAccessToken } = await import('../slices/userSlice');
                (store as any).dispatch(setAccessToken(response.accessToken));

                // Retry the original request with new token
                originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
                return apiClient(originalRequest);
              }
            } catch (refreshError) {
              logger.error('Token refresh failed', refreshError as Error);
              // Fall through to show session expired message
            }
          }

          // If refresh failed, show session expired toast once
          if (!sessionExpiredToastShown) {
            sessionExpiredToastShown = true;
            showErrorToast('Session expired', 'Session expired. Please login again');

            // Small delay to let user see the toast
            setTimeout(async () => {
              // Only clear keys owned by this application to avoid affecting other apps on the same domain
              const { clearAppStorage } = await import('../../utils/storage');
              clearAppStorage();

              window.location.href = '/login';
            }, 1000);
          }
        }
        break;
      default:
        if (!reqData?.skipToastMsg) {
          const message = reqData?.message;
          const errors = reqData?.errors;
          showErrorToast(message, errors);
        }
    }

    return Promise.reject({
      message: reqData?.message || error.message || 'Unknown error',
      status,
      data: reqData,
    });
  },
);

// ---------------------
// Base Query for RTK Query
// ---------------------
export const axiosBaseQuery =
  (): BaseQueryFn<AxiosBaseQueryArgs, unknown, unknown> =>
  async ({ url, method, data, params, contentType, successToast: _successToast }) => {
    try {
      const config: AxiosRequestConfig & {
        contentType?: 'MULTIPART' | 'JSON';
      } = {
        url,
        method,
        data,
        params,
        contentType,
        headers: contentType === 'MULTIPART' ? undefined : { 'Content-Type': 'application/json' },
      };

      const result = await apiClient(config);
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export default apiClient;
